import { Box, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";
import { PATHS } from "constants/pathConstants";
import ArrowForward from "@mui/icons-material/ArrowForward";
import GLButton from "components/v3/sharedComponents/GLButton";
import WhatsNextBox from "components/v2/Pages/TestResultsPage/WhatsNextBox";
import TestResultsDNA from "assets/images/test-results-dna-strand.svg";
import { RESULTS_CONFIGS } from "components/v2/Pages/TestResultsPage";
import { trackResultsReviewDecision } from "analytics";
export interface Slide {
  id: string;
  content: React.ReactNode;
  wideModeSideContent?: React.ReactNode;
}

export const createMoreInfoIntroSlide = (classes: any): Slide => ({
  id: "more-info-intro-slide",
  content: (
    <Box className={classes.slideContent}>
      <img
        src={TestResultsDNA}
        alt="Test Results DNA"
        className={classes.narrowImage}
      />
      <Typography variant="h6_black" sx={{ mb: 2 }}>
        It is important to understand what testing was done when you look at
        your results.
      </Typography>
    </Box>
  ),
  wideModeSideContent: TestResultsDNA
});

export const createMoreInfoOutroSlide = (
  classes: any,
  resultsUrl: string,
  history: ReturnType<typeof useHistory>
): Slide => ({
  id: "more-info-outro-slide",
  content: (
    <Box className={classes.slideContent}>
      <Typography variant="h6_black" sx={{ mb: 2, mt: 0 }}>
        There are some steps you start taking
      </Typography>
      <Typography variant="body_black" style={{ fontSize: "14px" }}>
        There are some recommended screenings and preventive things you can
        start doing.
      </Typography>
      <div className={classes.narrowImage}>
        <WhatsNextBox smallMode={false} />
      </div>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          position: "absolute",
          bottom: "70px",
          width: "100%"
        }}
      >
        <GLButton
          sx={{
            height: "60px"
          }}
          rightIcon={<ArrowForward style={{ fontSize: "18px" }} />}
          buttonType="primary"
          onClick={() => {
            history.push("/content/help-center/topics/health-library/");
          }}
        >
          Screening & healthy lifestyle resources
        </GLButton>
        <GLButton
          buttonType="secondary-no-border"
          onClick={() => {
            history.push(`${resultsUrl}#fileListingContainer`);
          }}
        >
          Back to my results
        </GLButton>
      </Box>
    </Box>
  ),
  wideModeSideContent: <WhatsNextBox smallMode={false} />
});

export const createLoginFirstTimeSlide = (user: any, classes: any): Slide => ({
  id: "login",
  content: (
    <Box className={classes.slideContent}>
      <Typography variant="h6_black" sx={{ mb: 2 }}>
        Hi {user?.preferredName},
        <br />
        <br />
        Your results are now available to view! Here are some important things
        to know before reviewing your results
      </Typography>
      <Typography
        variant="body_black"
        style={{ fontSize: "15px", fontStyle: "italic" }}
      >
        Your results are not a diagnosis. This test looks at changes a number of
        genes that may put you at higher risk for certain conditions.
      </Typography>
    </Box>
  ),
  wideModeSideContent: TestResultsDNA
});

export const createGeneCountSlide = (classes: any): Slide => ({
  id: "gene-count",
  content: (
    <Box className={classes.slideContent}>
      <img
        src={TestResultsDNA}
        alt="Test Results DNA"
        className={classes.narrowImage}
      />
      <Typography variant="h6_black" sx={{ mb: 2 }}>
        The test looked at{" "}
        <Typography
          variant="h6_serif_accent2"
          style={{ fontWeight: "bold" }}
          component="span"
        >
          163 genes
        </Typography>{" "}
        for changes that might raise the risk of some cancers, heart conditions,
        and other conditions. The conditions have actions and preventive steps.
      </Typography>
      <Typography
        variant="body_black"
        style={{ fontSize: "15px", fontStyle: "italic" }}
      >
        Uncertain genetic changes are not included in this report.
      </Typography>
    </Box>
  ),
  wideModeSideContent: TestResultsDNA
});

export const createReadyToViewResultsQuestionSlide = (
  classes: any,
  history: ReturnType<typeof useHistory>,
  onAdvance?: () => void
): Slide => ({
  id: "ready-to-view-results-question",
  content: (
    <Box className={classes.slideContent}>
      <img
        src={TestResultsDNA}
        alt="Test Results DNA"
        className={classes.narrowImage}
      />
      <Typography variant="body_black" sx={{ mb: 6 }}>
        If there is a finding on your report that may directly impact your
        health, a Navigator will be reaching out to you to review your result
        and discuss next steps.
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          position: "absolute",
          bottom: "70px",
          width: "100%"
        }}
      >
        <Typography variant="h6_black" sx={{ mb: 2 }}>
          Are you ready to view your results?
        </Typography>
        <GLButton
          rightIcon={<ArrowForward style={{ fontSize: "18px" }} />}
          buttonType="primary"
          onClick={() => {
            trackResultsReviewDecision("view_now");
            if (onAdvance) onAdvance();
          }}
        >
          I'm ready to view my results
        </GLButton>
        <GLButton
          buttonType="secondary-no-border"
          onClick={() => {
            trackResultsReviewDecision("defer");
            history.push(PATHS.LANDING_PAGE);
          }}
        >
          No, I'm not ready
        </GLButton>
      </Box>
    </Box>
  ),
  wideModeSideContent: TestResultsDNA
});

export const createResultsSlide = (
  riskAssessment: any,
  classes: any,
  history: ReturnType<typeof useHistory>,
  resultsUrl: string
): Slide => {
  const getResultConfig = (result: string) => {
    return (
      RESULTS_CONFIGS[result.toLowerCase() as keyof typeof RESULTS_CONFIGS] || {
        icon: null,
        description: "",
        subText: ""
      }
    );
  };

  return {
    id: "results-slide",
    content: (
      <Box className={classes.slideContent}>
        <img
          src={TestResultsDNA}
          alt="Test Results DNA"
          className={classes.narrowImage}
        />
        <Typography variant="h6_black" sx={{ mb: 2 }}>
          Your results:{" "}
          {riskAssessment[0].topLineResult.charAt(0).toUpperCase() +
            riskAssessment[0].topLineResult.slice(1)}
        </Typography>
        <Typography variant="body_black" sx={{ mb: 2 }}>
          {getResultConfig(riskAssessment[0].topLineResult).description}
        </Typography>
        <Typography
          variant="body_black"
          style={{ fontSize: "15px", fontStyle: "italic" }}
        >
          {getResultConfig(riskAssessment[0].topLineResult).subText}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            mt: 5
          }}
        >
          <GLButton
            rightIcon={<ArrowForward style={{ fontSize: "18px" }} />}
            buttonType="primary"
            onClick={() => {
              trackResultsReviewDecision("go_to_files");
              history.push(`${resultsUrl}#fileListingContainer`);
            }}
          >
            Take me to my files
          </GLButton>
          <GLButton
            buttonType="secondary-no-border"
            onClick={() => {
              trackResultsReviewDecision("return_dashboard");
              history.push(PATHS.LANDING_PAGE);
            }}
          >
            Back to my dashboard
          </GLButton>
        </Box>
      </Box>
    ),
    wideModeSideContent: TestResultsDNA
  };
};

// Utility function to compose slides in any order
export const composeSlides = (
  user: any,
  riskAssessment: any,
  history: ReturnType<typeof useHistory>,
  classes: any,
  resultsUrl: string,
  slideIds: string[],
  onAdvance?: () => void
): Slide[] => {
  const slideMap = {
    "more-info-intro": () => createMoreInfoIntroSlide(classes),
    "more-info-outro": () =>
      createMoreInfoOutroSlide(classes, resultsUrl, history),
    login: () => createLoginFirstTimeSlide(user, classes),
    "gene-count": () => createGeneCountSlide(classes),
    "ready-to-view-results-question": () =>
      createReadyToViewResultsQuestionSlide(classes, history, onAdvance),
    results: () =>
      createResultsSlide(riskAssessment, classes, history, resultsUrl)
  };

  return slideIds.map((id) => slideMap[id as keyof typeof slideMap]());
};

// For backward compatibility
export const createSlides = (
  user: any,
  riskAssessment: any,
  history: ReturnType<typeof useHistory>,
  classes: any,
  resultsUrl: string,
  onAdvance?: () => void
) => {
  return composeSlides(user, riskAssessment, history, classes, resultsUrl, [
    "more-info-intro",
    "more-info-outro",
    "login",
    "gene-count",
    "ready-to-view-results-question",
    "results"
  ]);
};
